import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Check } from 'lucide-react';

import Card3D from '../Card3D';

const ProjectModal = ({ project, onClose }) => {
  const { t } = useTranslation();

  const [currentIndex, setCurrentIndex] = useState(0);

  /* project 변경 시 초기화 */

  useEffect(() => {
    setCurrentIndex(0);
  }, [project]);

  /* ESC CLOSE */

  useEffect(() => {
    if (!project) return;

    const handleEsc = (e) => {
      if (e.key === 'Escape') onClose();
    };

    window.addEventListener('keydown', handleEsc);

    return () => window.removeEventListener('keydown', handleEsc);
  }, [project, onClose]);

  /* BODY SCROLL LOCK */

  useEffect(() => {
    if (!project) return;

    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [project]);

  if (!project) return null;

  const isWebsite = project.type === 'website';
  const isCard = project.type === 'card';

  return (
    <div
      className='fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 bg-black/60 backdrop-blur-sm'
      onClick={onClose}
    >
      <div
        className='
          bg-white
          rounded-2xl

          max-w-6xl
          w-full

          min-h-[70vh]
          sm:h-[90vh]

          shadow-2xl
          relative

          flex
          flex-col

          overflow-hidden
        '
        onClick={(e) => e.stopPropagation()}
      >
        {/* CLOSE BUTTON */}

        <button
          onClick={onClose}
          className='absolute z-20 flex items-center justify-center transition bg-white border rounded-full top-4 right-4 w-9 h-9 border-neutral-200 hover:bg-neutral-100'
        >
          <X size={18} />
        </button>

        {/* ================= TOP INFO ================= */}

        <div className='flex-shrink-0 px-5 py-3 border-b border-neutral-200'>
          <div className='flex gap-4 overflow-x-auto scrollbar-hide md:grid md:grid-cols-3 md:gap-6'>
            <CompactInfo
              title={t('portfolioDetail.overviewTitle')}
              text={t(project.overview)}
            />

            <CompactInfo
              title={t('portfolioDetail.directionTitle')}
              text={t(project.direction)}
            />

            <CompactInfo
              title={t('portfolioDetail.resultTitle')}
              text={t(project.result)}
            />
          </div>
        </div>

        {/* ================= CONTENT ================= */}

        <div
          className='
            grid
            grid-rows-[1fr_auto]

            flex-1
            min-h-0
          '
        >
          {isWebsite ? (
            <iframe
              src={project.url}
              title={t(project.title)}
              className='w-full h-full border-0'
            />
          ) : isCard ? (
            <Card3D
              frontImage={project.selectedCard?.front}
              backImage={project.selectedCard?.back}
            />
          ) : (
            <>
              {/* MAIN IMAGE */}

              <div className='flex items-center justify-center p-4 overflow-hidden sm:p-6'>
                <img
                  src={project.images?.[currentIndex]}
                  alt={t(project.title)}
                  className='object-contain max-w-full max-h-full transition duration-300 '
                />
              </div>

              {/* THUMBNAIL GALLERY */}

              {project.images?.length > 1 && (
                <div className='flex gap-3 px-4 pt-4 pb-2 overflow-x-auto border-t border-neutral-200'>
                  {project.images.map((img, i) => {
                    const isActive = currentIndex === i;

                    return (
                      <button
                        key={i}
                        onClick={() => setCurrentIndex(i)}
                        className='relative flex-shrink-0 transition duration-200 hover:scale-105'
                      >
                        <div className='relative overflow-hidden rounded-xl'>
                          <img
                            src={img}
                            alt=''
                            className='block object-cover h-20 aspect-video'
                          />

                          {/* DARK OVERLAY */}

                          <div
                            className={`
                              absolute
                              inset-0
                              transition
                              duration-200
                              ${
                                isActive
                                  ? 'bg-black/40'
                                  : 'bg-black/0 hover:bg-black/10'
                              }
                            `}
                          />

                          {/* CHECK ICON */}

                          {isActive && (
                            <div className='absolute inset-0 flex items-center justify-center'>
                              <div className='flex items-center justify-center bg-white rounded-full shadow-md w-7 h-7'>
                                <Check size={16} strokeWidth={3} />
                              </div>
                            </div>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProjectModal;

/* ================= INFO BLOCK ================= */

const CompactInfo = ({ title, text }) => (
  <div
    className='
      min-w-[220px]

      md:min-w-0
    '
  >
    <p
      className='
        mb-1

        text-[10px]
        tracking-wider
        uppercase

        text-neutral-400
      '
    >
      {title}
    </p>

    <p className='text-xs leading-relaxed md:text-sm text-neutral-600 line-clamp-2 md:line-clamp-3'>
      {text}
    </p>
  </div>
);
